import { useState } from "react";
import { Alert, Button, Drawer, Input, Modal, Space, Typography } from "antd";
import { CheckOutlined, CloseOutlined, DownloadOutlined, ExportOutlined, UploadOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { EvidenceDto } from "@audit-plan/shared";
import { useAuth } from "../../auth/AuthContext";
import { usePeople } from "../../api/people";
import { invalidateAudit, useAudit } from "../../api/audits";
import { evidenceDownloadUrl, fileExt, reviewEvidence } from "../../api/evidence";
import { EvidenceStatusPill, fmtDT } from "../pills";
import { useNotify } from "../audit/useNotify";
import { useFullDrawer, useFullModal } from "../audit/mobileModal";
import { useIsMobile } from "../useIsMobile";
import { UploadModal, useTargetLabel } from "./UploadModal";
import { ResizableTable } from "../ResizableTable";

export const fmtBytes = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

/** Drawer: anteprima della pagina dell'evidenza (/evidence/:id), con «Apri pagina». */
export function EvidenceDrawer(props: { evidence: EvidenceDto | null; onClose: () => void; onChanged?: () => void }) {
  return <EvidenceDetail {...props} mode="drawer" />;
}

/** Contenuto dell'evidenza: nel drawer o nella pagina con URL. */
export function EvidenceDetail(props: { evidence: EvidenceDto | null; onClose: () => void; onChanged?: () => void; mode: "drawer" | "page" }) {
  const { evidence: e, onClose, onChanged, mode } = props;
  const { t } = useTranslation();
  const { can } = useAuth();
  const people = usePeople();
  const qc = useQueryClient();
  const n = useNotify();
  const audit = useAudit(e?.auditId).data;
  const label = useTargetLabel(e?.auditId);
  const [preview, setPreview] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [newVer, setNewVer] = useState(false);
  const mobile = useIsMobile();
  const fd = useFullDrawer(640);
  const fm = useFullModal();

  if (!e) return mode === "drawer" ? <Drawer open={false} onClose={onClose} /> : null;
  const last = e.versions[e.versions.length - 1];
  const ext = fileExt(e.name);
  const canPreview = ["pdf", "jpg", "jpeg", "png"].includes(ext) && !!last;
  const canReview = e.status === "pending" && !!audit && can("action.review", { auditorId: audit.auditorId, supplierId: e.supplierId });
  // un documento d'archivio (audit storico) non si versiona
  const canUp = can("evidence.upload", { supplierId: e.supplierId }) && !audit?.historical;
  const done = () => {
    invalidateAudit(qc, e.auditId ?? undefined);
    onChanged?.();
  };
  const review = async (status: "approved" | "rejected", text?: string) => {
    try {
      await reviewEvidence(e.id, status, text);
      n.ok(t(status === "approved" ? "ev_approved" : "ev_rejected"));
      setRejecting(false);
      setNote("");
      done();
    } catch (err) {
      n.fail(err);
    }
  };

  const bs = mobile ? { size: "large" as const, style: { height: 48 } } : {};
  const actions = (
    <Space wrap style={mobile ? { width: "100%" } : undefined}>
      {mode === "drawer" ? (
        <Link to={`/evidence/${e.id}`} onClick={onClose}>
          <Button {...bs} icon={<ExportOutlined />}>{t("f_openPage")}</Button>
        </Link>
      ) : null}
      {last ? (
        <Button {...bs} icon={<DownloadOutlined />} href={evidenceDownloadUrl(e.id, last.v)}>
          {t("download")}
        </Button>
      ) : null}
      {canPreview ? <Button {...bs} onClick={() => setPreview((p) => !p)}>{t("preview")}</Button> : null}
      {canUp ? (
        <Button {...bs} icon={<UploadOutlined />} onClick={() => setNewVer(true)}>
          {t("newVersion")}
        </Button>
      ) : null}
      {canReview ? (
        <>
          <Button {...bs} danger icon={<CloseOutlined />} onClick={() => setRejecting(true)}>
            {t("reject")}
          </Button>
          <Button {...bs} type="primary" icon={<CheckOutlined />} onClick={() => review("approved")}>
            {t("approve")}
          </Button>
        </>
      ) : null}
    </Space>
  );

  const body = (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        {mode === "page" ? actions : null}
        <div>
          <EvidenceStatusPill s={e.status} />
          <div>
            <Typography.Text type="secondary">
              {audit ? `${audit.supplierName} · ${audit.code} · ` : ""}
              {t("linkedTo")}: {label(e.target)}
            </Typography.Text>
          </div>
        </div>
        {e.description ? <Typography.Paragraph>{e.description}</Typography.Paragraph> : null}
        {e.reviewNote ? <Alert type="warning" message={`${t("reviewNote")}: ${e.reviewNote}`} /> : null}
        {preview && last ? (
          ext === "pdf" ? (
            <iframe title={e.name} src={evidenceDownloadUrl(e.id, last.v, true)} style={{ width: "100%", height: "60vh", border: "1px solid #E2DED3", borderRadius: 10 }} />
          ) : (
            <img alt={e.name} src={evidenceDownloadUrl(e.id, last.v, true)} style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: 10, border: "1px solid #E2DED3" }} />
          )
        ) : null}
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t("versions")}
        </Typography.Title>
        <ResizableTable
          size="small"
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey="id"
          dataSource={[...e.versions].reverse()}
          columns={[
            { title: "v", dataIndex: "v", render: (v: number) => `v${v}` },
            { title: t("uploadedBy"), dataIndex: "uploadedById", render: (id: string | null) => people.show(id) },
            { title: t("date"), dataIndex: "createdAt", render: (d: string) => fmtDT(d) },
            { title: t("b_size"), dataIndex: "size", render: (s: number) => fmtBytes(s) },
            {
              title: "",
              align: "right" as const,
              render: (_: unknown, v: EvidenceDto["versions"][number]) => (
                <Button size="small" icon={<DownloadOutlined />} href={evidenceDownloadUrl(e.id, v.v)}>
                  {t("download")}
                </Button>
              ),
            },
          ]}
        />
      </Space>
      <Modal {...fm} open={rejecting} title={`${t("reject")} · ${e.name}`} onCancel={() => setRejecting(false)} okText={t("reject")} cancelText={t("cancel")} okButtonProps={{ danger: true, disabled: !note.trim() }} onOk={() => review("rejected", note.trim())}>
        <Typography.Text>{t("reviewNote")} *</Typography.Text>
        <Input.TextArea rows={3} value={note} onChange={(ev) => setNote(ev.target.value)} />
      </Modal>
      <UploadModal open={newVer} onClose={() => setNewVer(false)} evidenceId={e.id} keepExt={ext} ctx={{ supplierId: e.supplierId, auditId: e.auditId, target: e.target }} onDone={done} />
    </>
  );
  if (mode === "page") return body;
  return (
    <Drawer
      open
      onClose={() => {
        setPreview(false);
        onClose();
      }}
      {...fd}
      title={<span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</span>}
      footer={mobile ? actions : undefined}
      extra={mobile ? null : actions}
    >
      {body}
    </Drawer>
  );
}
